import { failure, success } from '@mntn/result'
import type { GaryClient } from '../../client/index.ts'
import {
  type CampaignGroupFilterInput,
  type CampaignGroupPublicStatusEnum,
  CampaignGroupRefTypeEnum,
  type GoalTypeEnum,
  type ObjectiveTypeEnum,
  type Select_CloneCampaignGroupExperiment,
  type Select_CloneCampaignGroupExperimentVariables,
  type Select_GetCampaignGroupById,
  type Select_GetCampaignGroupByIdVariables,
  type Select_GetCampaignGroups,
  type Select_GetCampaignGroupsByExternalRefIds,
  type Select_GetCampaignGroupsByPrivateMarketplaceGroupId,
} from '../../graphql/generated.types.ts'
import { mutations, queries } from './campaigns.graphql.ts'
import { getSpendErrorRowIndices } from './get-spend-error-row-indices.ts'

export {
  CampaignGroupActionEnum,
  GoalTypeEnum,
} from '../../graphql/generated.types.ts'

export const SELECT_PRODUCT_ID = 2

type CampaignGroup = NonNullable<Select_GetCampaignGroups['getCampaignGroups']>['rows'][0] & {
  is_test?: boolean | null
}

type CampaignGroupByExternalRefIds = NonNullable<
  Select_GetCampaignGroupsByExternalRefIds['getCampaignGroupsByExternalRefIds']
>

type CampaignGroupByExternalRefId = CampaignGroupByExternalRefIds['rows'][0]

type CampaignGroupByPrivateMarketplaceGroupId = NonNullable<
  Select_GetCampaignGroupsByPrivateMarketplaceGroupId['getCampaignGroupsByPrivateMarketplaceGroupId']
>[0]

type CampaignGroupMetadataUpdateInput = {
  config_override_id: number
  group_by_ref: boolean
}

type CampaignGroupChannelMarginsInput = {
  channel_id: number
  margin_source_id: number
  margin_reason_id: number
  budget_margin: number
  ad_buying_cpm: number
  target_cpm: number
  user_id: number
}

type CampaignGroupPrivateMarketplaceDealInput = {
  group_id: number
}

type CampaignGroupUpdateInput = {
  name: string
  is_test: boolean
  campaign_goal_id: number
  campaign_template_group_id: number
  campaign_group_private_marketplace_deals: CampaignGroupPrivateMarketplaceDealInput
  campaign_group_channel_margins: CampaignGroupChannelMarginsInput
  campaign_group_metadata: CampaignGroupMetadataUpdateInput
  ref_id?: number
  ref_type?: CampaignGroupRefTypeEnum
  goal_type: GoalTypeEnum
  goal_value: number
  audience_id: number | null
  campaign_partner_id: number | null
  delivery_mode_id?: number
  platform?: 'ptv'
}

export type CreateCampaignGroupInput = {
  advertiserId: number
  objectiveType: ObjectiveTypeEnum
  update: CampaignGroupUpdateInput
}

export type CloneCampaignGroupExperimentInput = {
  advertiserId: number
  clonedFromCampaignGroupId: number
  update: CampaignGroupUpdateInput
}

type Select_CreateCampaignGroup = {
  createCampaignGroup?: {
    campaign_group_id: number
    product_id?: number | null
  } | null
}

type Select_CreateCampaignGroupVariables = {
  advertiser_id: number
  objective_type: ObjectiveTypeEnum
  update: CampaignGroupUpdateInput
}

type Select_GetCampaignGroupConfiguration = {
  getCampaignGroupById?: {
    campaign_group_id: number
    advertiser_id: number
    status: CampaignGroupPublicStatusEnum
    delivery_mode_id?: number | null
    product_id?: number | null
    is_test?: boolean | null
    experiment_cloned_from?: { campaign_group_id: number } | null
  } | null
  getAudienceByCampaignGroupId?: { audience_id: number } | null
}

type Select_GetCampaignGroupConfigurationVariables = {
  campaign_group_id: number
}

const transformCampaignGroupWithDetails = (
  cg: CampaignGroupByPrivateMarketplaceGroupId | CampaignGroupByExternalRefId,
  options?: {
    hasSpendError?: boolean
    allocatableEffectiveBudget?: number | null
  },
) => {
  const orderItemId = cg.ref_type === CampaignGroupRefTypeEnum.SELECT_ORDER_ITEM ? cg.ref_id : null

  return {
    campaignGroupId: cg.campaign_group_id,
    advertiserId: cg.advertiser_id,
    name: cg.name,
    effectiveBudget: cg.budget_summary?.effective_budget ?? null,
    ...(options?.allocatableEffectiveBudget !== undefined
      ? { allocatableEffectiveBudget: options.allocatableEffectiveBudget }
      : {}),
    ineffectiveBudget: cg.budget_summary?.ineffective_budget ?? null,
    totalBudget: cg.budget_summary?.total_budget ?? null,
    spend: cg.spend ?? null,
    startTime: cg.start_time ?? null,
    endTime: cg.end_time ?? null,
    status: cg.status,
    orderItemId,
    availableActions: cg.available_actions ?? null,
    uiCreativesStatus: cg.ui_creatives_status,
    isTest: cg.is_test ?? null,
    hasSpendError: options?.hasSpendError ?? false,
  }
}

export const createCampaignsService = (client: GaryClient) => ({
  createCampaignGroup: async ({ advertiserId, objectiveType, update }: CreateCampaignGroupInput) => {
    try {
      const { data } = await client.mutate<Select_CreateCampaignGroup, Select_CreateCampaignGroupVariables>({
        mutation: mutations.CREATE_CAMPAIGN_GROUP,
        variables: {
          advertiser_id: advertiserId,
          objective_type: objectiveType,
          update,
        },
      })

      const campaignGroup = data?.createCampaignGroup

      if (!campaignGroup) {
        return failure({
          customCode: 'CAMPAIGN_GROUP_CREATE_ERROR',
          message: 'Failed to create campaign group',
          payload: { advertiserId },
        })
      }

      if (campaignGroup.product_id == null) {
        return failure({
          customCode: 'CAMPAIGN_GROUP_CREATE_ERROR',
          message: 'Failed to create campaign group: missing product id',
          payload: { advertiserId },
        })
      }

      return success(
        {
          campaignGroupId: campaignGroup.campaign_group_id,
          productId: campaignGroup.product_id,
        },
        { event: 'gary_client.campaigns.create_campaign_group' },
      )
    } catch (error) {
      return failure({
        customCode: 'CAMPAIGN_GROUP_CREATE_ERROR',
        message: 'Error creating campaign group',
        payload: { advertiserId },
        cause: error,
      })
    }
  },

  getCampaignGroupConfiguration: async (campaignGroupId: number) => {
    try {
      const { data } = await client.query<
        Select_GetCampaignGroupConfiguration,
        Select_GetCampaignGroupConfigurationVariables
      >({
        query: queries.GET_CAMPAIGN_GROUP_CONFIGURATION,
        variables: { campaign_group_id: campaignGroupId },
      })

      const campaignGroup = data.getCampaignGroupById
      const audience = data.getAudienceByCampaignGroupId

      if (
        !campaignGroup ||
        campaignGroup.delivery_mode_id == null ||
        campaignGroup.product_id == null ||
        campaignGroup.is_test == null ||
        !audience
      ) {
        return failure({
          customCode: 'CAMPAIGN_GROUP_CONFIGURATION_FETCH_ERROR',
          message: 'Campaign group configuration is incomplete',
          payload: { campaignGroupId },
        })
      }

      return success(
        {
          advertiserId: campaignGroup.advertiser_id,
          audienceId: audience.audience_id,
          deliveryModeId: campaignGroup.delivery_mode_id,
          productId: campaignGroup.product_id,
          isTest: campaignGroup.is_test,
          status: campaignGroup.status,
          clonedFromCampaignGroupId: campaignGroup.experiment_cloned_from?.campaign_group_id ?? null,
        },
        { event: 'gary_client.campaigns.get_configuration' },
      )
    } catch (error) {
      return failure({
        customCode: 'CAMPAIGN_GROUP_CONFIGURATION_FETCH_ERROR',
        message: 'Error fetching campaign group configuration',
        payload: { campaignGroupId },
        cause: error,
      })
    }
  },

  cloneCampaignGroupExperiment: async ({
    advertiserId,
    clonedFromCampaignGroupId,
    update,
  }: CloneCampaignGroupExperimentInput) => {
    try {
      const { data } = await client.mutate<
        Select_CloneCampaignGroupExperiment,
        Select_CloneCampaignGroupExperimentVariables
      >({
        mutation: mutations.CLONE_CAMPAIGN_GROUP_EXPERIMENT,
        variables: {
          campaign_group_id: clonedFromCampaignGroupId,
          update,
        },
      })

      const campaignGroup = data?.cloneCampaignGroupById
      if (!campaignGroup || campaignGroup.product_id == null) {
        return failure({
          customCode: 'CAMPAIGN_GROUP_CREATE_ERROR',
          message: 'Failed to clone campaign group experiment',
          payload: { advertiserId },
        })
      }

      return success(
        {
          campaignGroupId: campaignGroup.campaign_group_id,
          productId: campaignGroup.product_id,
        },
        { event: 'gary_client.campaigns.clone_campaign_group_experiment' },
      )
    } catch (error) {
      return failure({
        customCode: 'CAMPAIGN_GROUP_CREATE_ERROR',
        message: 'Error cloning campaign group experiment',
        payload: { advertiserId },
        cause: error,
      })
    }
  },

  getCampaignGroupById: async (campaignGroupId: number) => {
    try {
      const { data } = await client.query<Select_GetCampaignGroupById, Select_GetCampaignGroupByIdVariables>({
        query: queries.GET_CAMPAIGN_GROUP_BY_ID,
        variables: { campaign_group_id: campaignGroupId },
      })

      if (data.getCampaignGroupById) {
        const cg = data.getCampaignGroupById
        const orderItemId = cg.ref_type === CampaignGroupRefTypeEnum.SELECT_ORDER_ITEM ? cg.ref_id : null

        return {
          campaignGroupId: cg.campaign_group_id,
          advertiserId: cg.advertiser_id,
          name: cg.name,
          isTest: cg.is_test ?? null,
          campaigns: cg.campaigns,
          effectiveBudget: cg.budget_summary?.effective_budget ?? null,
          ineffectiveBudget: cg.budget_summary?.ineffective_budget ?? null,
          totalBudget: cg.budget_summary?.total_budget ?? null,
          spend: cg.spend ?? null,
          startTime: cg.start_time ?? null,
          endTime: cg.end_time ?? null,
          status: cg.status,
          orderItemId,
          availableActions: cg.available_actions ?? null,
          uiCreativesStatus: cg.ui_creatives_status,
        }
      }
    } catch (error) {
      console.error('Error fetching campaign by ID:', error)
      throw error
    }
  },

  getCampaignGroups: async ({
    page,
    limit,
    sortBy,
    sortOrder,
    filter,
  }: {
    page: number
    limit: number
    sortBy: string
    sortOrder: string
    filter?: CampaignGroupFilterInput
  }) => {
    try {
      const { data } = await client.query({
        query: queries.GET_CAMPAIGN_GROUPS,
        variables: {
          page,
          limit,
          sort_by: sortBy,
          sort_order: sortOrder,
          filter,
        },
      })

      if (data.getCampaignGroups?.rows) {
        const transformedRows = data.getCampaignGroups.rows.map((cg: CampaignGroup) => {
          const orderItemId = cg.ref_type === CampaignGroupRefTypeEnum.SELECT_ORDER_ITEM ? cg.ref_id : null
          return {
            campaignGroupId: cg.campaign_group_id,
            name: cg.name,
            isTest: cg.is_test ?? null,
            effectiveBudget: cg.budget_summary?.effective_budget ?? null,
            ineffectiveBudget: cg.budget_summary?.ineffective_budget ?? null,
            totalBudget: cg.budget_summary?.total_budget ?? null,
            startTime: cg.start_time ?? null,
            endTime: cg.end_time ?? null,
            status: cg.status,
            orderItemId,
            availableActions: cg.available_actions ?? null,
          }
        })

        return {
          ...data.getCampaignGroups,
          rows: transformedRows,
        }
      }

      return data.getCampaignGroups
    } catch (error) {
      console.error('Error fetching campaign groups:', error)
      throw error
    }
  },

  getAccessibleCampaignGroupIds: async () => {
    try {
      const pageSize = 1000 // Large page size since we're only fetching IDs
      let page = 0
      let allCampaignIds: number[] = []
      let hasMore = true

      while (hasMore) {
        const { data } = await client.query({
          query: queries.GET_ACCESSIBLE_CAMPAIGN_GROUP_IDS,
          variables: {
            page,
            limit: pageSize,
            filter: {
              //@note product_id is required to get select campaigns
              product_id: SELECT_PRODUCT_ID,
            },
          },
        })

        const campaignIds =
          data.getCampaignGroups?.rows?.map((row: { campaign_group_id: number }) => row.campaign_group_id) || []

        allCampaignIds = [...allCampaignIds, ...campaignIds]

        hasMore = campaignIds.length === pageSize
        page++
      }

      return allCampaignIds
    } catch (error) {
      console.error('❌ [CampaignsAccess] Error fetching accessible campaign group IDs:', {
        error: error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
      })
      throw error
    }
  },
  getCampaignGroupsByExternalRefIds: async (
    refIds: number[],
    refType: CampaignGroupRefTypeEnum,
    options?: {
      filter?: CampaignGroupFilterInput
      limit?: number
      page?: number
      propagateReportingErrors?: boolean
    },
  ) => {
    const transformedFilter = {
      ...options?.filter,
      //@note product_id is required to get select campaigns
      product_id: SELECT_PRODUCT_ID,
    }

    try {
      const propagateReportingErrors = options?.propagateReportingErrors ?? false
      const { data, errors } = await client.query({
        query: queries.GET_CAMPAIGN_GROUPS_BY_EXTERNAL_REF_IDS,
        variables: {
          ref_ids: refIds,
          ref_type: refType,
          filter: transformedFilter,
          sort_by: 'name',
          sort_order: 'ASC',
          page: options?.page ?? 0,
          limit: options?.limit,
          propagateReportingErrors,
        },
        errorPolicy: propagateReportingErrors ? 'all' : 'none',
      })

      // return early if no campaign groups found
      if (!data?.getCampaignGroupsByExternalRefIds?.rows || data.getCampaignGroupsByExternalRefIds.rows.length === 0) {
        return success([], {
          event: 'gary_client.campaigns.get_campaign_groups_by_external_ref_ids',
        })
      }

      const campaignGroups = data.getCampaignGroupsByExternalRefIds.rows
      const spendErrorRowIndices = propagateReportingErrors ? getSpendErrorRowIndices(errors) : new Set<number>()

      return success(
        campaignGroups.map((cg: CampaignGroupByExternalRefId, index: number) => {
          return transformCampaignGroupWithDetails(cg, {
            hasSpendError: spendErrorRowIndices.has(index),
            allocatableEffectiveBudget: cg.budget_summary?.allocatable_effective_budget ?? null,
          })
        }),
        {
          event: 'gary_client.campaigns.get_campaign_groups_by_external_ref_ids',
        },
      )
    } catch (error) {
      /** Both exits below report the same failure; only the fallback's own cause differs. */
      const fail = (fallbackCause?: unknown) =>
        failure(
          {
            customCode: 'CAMPAIGN_GROUPS_BY_EXTERNAL_REF_IDS_FETCH_ERROR',
            message: 'Error fetching campaign groups by external ref ids',
            payload: {
              refIds,
              refType,
              options,
            },
          },
          {
            cause: error,
            ...(fallbackCause !== undefined && { fallbackCause }),
          },
        )

      if (!options?.propagateReportingErrors) {
        return fail()
      }

      try {
        const { data } = await client.query({
          query: queries.GET_CAMPAIGN_GROUPS_BY_EXTERNAL_REF_IDS,
          variables: {
            ref_ids: refIds,
            ref_type: refType,
            filter: transformedFilter,
            sort_by: 'name',
            sort_order: 'ASC',
            page: options?.page ?? 0,
            limit: options?.limit,
            propagateReportingErrors: false,
          },
        })

        const fallbackCampaignGroups = data?.getCampaignGroupsByExternalRefIds?.rows ?? []

        return success(
          fallbackCampaignGroups.map((cg: CampaignGroupByExternalRefId) =>
            transformCampaignGroupWithDetails(cg, {
              hasSpendError: true,
              allocatableEffectiveBudget: cg.budget_summary?.allocatable_effective_budget ?? null,
            }),
          ),
          {
            event: 'gary_client.campaigns.get_campaign_groups_by_external_ref_ids',
          },
        )
      } catch (fallbackCause) {
        // The fallback's own error is the second half of why this lookup produced
        // nothing, so it is reported rather than dropped.
        return fail(fallbackCause)
      }
    }
  },
  getCampaignGroupsByPrivateMarketplaceGroupId: async (privateMarketplaceGroupId: number) => {
    try {
      const { data } = await client.query({
        query: queries.GET_CAMPAIGN_GROUPS_BY_PRIVATE_MARKETPLACE_GROUP_ID,
        variables: {
          private_marketplace_group_id: privateMarketplaceGroupId,
        },
      })

      const campaignGroups: CampaignGroupByPrivateMarketplaceGroupId[] =
        data.getCampaignGroupsByPrivateMarketplaceGroupId

      return success(
        campaignGroups.map((cg: CampaignGroupByPrivateMarketplaceGroupId) => transformCampaignGroupWithDetails(cg)),
        {
          event: 'gary_client.campaigns.get_campaign_groups_by_private_marketplace_group_id',
        },
      )
    } catch {
      return failure({
        customCode: 'CAMPAIGN_GROUPS_BY_PRIVATE_MARKETPLACE_GROUP_ID_FETCH_ERROR',
        message: 'Error fetching campaign groups by private marketplace group id',
        payload: {
          privateMarketplaceGroupId,
        },
      })
    }
  },
})
